import React, { useRef, useState } from 'react';
import { array, bool, object } from 'prop-types';
import classNames from 'classnames/bind';
import { Tabs, TabPanel } from 'yoda-core-components/lib/components/Tabs/Tabs';
import Content from 'yoda-core-components/lib/components/Tabs/Content';
import Image from 'yoda-core-components/lib/components/Image';
import Title from 'yoda-core-components/lib/components/Tabs/Title';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import { sizeChartModalOptions } from '../../common/Constants';
import SizeChartTableConnected from './Components/SizeChartTable/SizeChartTable';
import * as styles from './SizeGuide.css';

const cx = classNames.bind(styles);

const myTheme = {
    tabActiveClass: styles.active,
    tabButtonClass: `${dt([
        'cursor-pointer',
        'inline-block',
        'no-underline',
        'p-2',
        'text-center',
        'text-medium',
    ])} ${cx('tabButton')}`,
    tabContainerClass: `${dt([
        'flex',
        'font-open-sans-regular',
        'mb-2',
        'mt-1',
        'mx-0',
        'text-gray-600',
        'text-medium',
    ])} ${cx('tab')}`,
    tabListItemClass: styles.tabListItem,
    tabPanelTitleClass: `${dt(['cursor-pointer', 'font-open-sans-regular', 'inline-block'])} ${cx(
        'tabPanelTitle'
    )}`,
};
export const textCase = (string) => string.charAt(0).toUpperCase() + string.slice(1);

const SizeGuide = ({ deviceType, sizeGuideOptions }) => {
    const { isTabbed = sizeGuideOptions.layoutStyle === 'TABBED', sections } = sizeGuideOptions;
    return (
        <>
            {isTabbed ? (
                <TabbedCharts sections={sections} deviceType={deviceType} />
            ) : (
                <NonTabbedCharts deviceType={deviceType} isTabbed={isTabbed} sections={sections} />
            )}
        </>
    );
};

const TabbedCharts = ({ sections, deviceType }) => (
    <Tabs activeTabPanelIndex={0} data-automation-id="tabbed-charts-tabs" themeConfig={myTheme}>
        {sections.map((section, i) => (
            <TabPanel title={section.name} index={i}>
                <Title>{section.name}</Title>
                <Content>
                    <Chart section={section} deviceType={deviceType} />
                </Content>
            </TabPanel>
        ))}
    </Tabs>
);

const NonTabbedCharts = ({ deviceType, isTabbed, sections }) =>
    sections.map((section) => (
        <Chart deviceType={deviceType} isTabbed={isTabbed} section={section} />
    ));

const Chart = ({ deviceType = {}, isTabbed = true, section = {} }) => {
    const { name, headerText, media, footerText } = section;
    const sizeMeasurements =
        section.sizeRangeMeasurement && section.sizeRangeMeasurement.sizeMeasurements;

    const sizeChartImageRef = useRef();
    const [sizeChartTableWidth, setSizeChartTableWidth] = useState();
    const [sizeChartImageWidth, setSizeChartImageWidth] = useState();

    // Image should float to the right of the chart if image width + chart width + necessary padding
    // is less than 90% of window size, which should be the max modal width
    const {
        maxPercentageModalWidth,
        paddingBetweenChartAndImage,
        totalModalPadding,
    } = sizeChartModalOptions;
    const totalModalSpaceAvailable =
        maxPercentageModalWidth * window.innerWidth - totalModalPadding;
    const shouldImageFloatRight =
        sizeChartTableWidth + sizeChartImageWidth + paddingBetweenChartAndImage <=
        totalModalSpaceAvailable;
    const spaceLeft =
        totalModalSpaceAvailable - (sizeChartTableWidth + paddingBetweenChartAndImage);

    const shouldStyleImageForFloat =
        shouldImageFloatRight && sizeChartTableWidth > 0 && !deviceType.isMobile;

    return (
        <>
            {name && (
                <div
                    className={`${dt([
                        'font-open-sans-semibold',
                        'mb-1',
                        'mt-2',
                        'text-gray-600',
                        'text-large',
                    ])}`}
                >
                    {!isTabbed && <span>{name}</span>}
                    {headerText && (
                        <div className={`${dt(['sm:my-1', 'sm:-mx-0', 'text-small'])}`}>
                            {headerText}
                        </div>
                    )}
                </div>
            )}
            <div className={`${dt([shouldImageFloatRight ? 'flex' : ''])}`}>
                <SizeChartTableConnected
                    sizeMeasurements={sizeMeasurements}
                    deviceType={deviceType}
                    updateChartWidth={setSizeChartTableWidth}
                />
                {media && (
                    <div style={shouldImageFloatRight ? { width: `${spaceLeft}px` } : null}>
                        <Image
                            alt="Size Chart"
                            ref={sizeChartImageRef}
                            src={media}
                            onLoad={() => setSizeChartImageWidth(sizeChartImageRef.current.width)}
                            className={
                                shouldStyleImageForFloat
                                    ? styles.sizeChartImageFloat
                                    : styles.sizeChartImage
                            }
                        />
                    </div>
                )}
            </div>
            {footerText && (
                <div className={`${dt(['font-open-sans-italic', 'text-xsmall'])}`}>
                    {footerText}
                </div>
            )}
        </>
    );
};

SizeGuide.propTypes = {
    sizeGuideOptions: object.isRequired,
    deviceType: object,
};

SizeGuide.defaultProps = {
    deviceType: {},
};

TabbedCharts.propTypes = {
    deviceType: object,
    sections: array,
};

TabbedCharts.defaultProps = {
    deviceType: {},
    sections: [],
};

Chart.propTypes = {
    deviceType: object,
    isTabbed: bool,
    section: object,
};

Chart.defaultProps = {
    deviceType: {},
    isTabbed: true,
    section: {},
};

export default SizeGuide;
